import React, { useEffect, useRef } from 'react';

// Loads TradingView's tv.js once and reuses it across mounts.
let tvScriptPromise = null;
function loadTradingView() {
  if (window.TradingView && window.TradingView.widget) return Promise.resolve();
  if (tvScriptPromise) return tvScriptPromise;
  tvScriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://s3.tradingview.com/tv.js';
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => { tvScriptPromise = null; reject(new Error('Failed to load TradingView')); };
    document.head.appendChild(s);
  });
  return tvScriptPromise;
}

const INTERVAL_MAP = {
  '1m': '1', '3m': '3', '5m': '5', '15m': '15', '30m': '30',
  '1h': '60', '2h': '120', '4h': '240', '1d': 'D', '1w': 'W'
};

// TradingView study ids — the free tv.js widget embed resolves studies by
// these SHORT internal ids (the long descriptive names only work in the full
// charting_library and produce "Cannot get study" warnings on the embed).
const STUDY_MAP = {
  ema: 'MAExp@tv-basicstudies',
  sma: 'MASimple@tv-basicstudies',
  bb: 'BB@tv-basicstudies',
  ichimoku: 'IchimokuCloud@tv-basicstudies',
  rsi: 'RSI@tv-basicstudies',
  macd: 'MACD@tv-basicstudies'
};

// Map our internal symbols to exchange-qualified TradingView symbols so every
// pair resolves to a real, live TradingView data feed.
const SYMBOL_OVERRIDES = {
  // Metals
  XAUUSD: 'OANDA:XAUUSD',
  XAGUSD: 'OANDA:XAGUSD',
  COPPER: 'CAPITALCOM:COPPER',
  // Energy
  WTI: 'TVC:USOIL',
  BRENT: 'TVC:UKOIL',
  NATGAS: 'CAPITALCOM:NATURALGAS',
  // Indices
  US30: 'CAPITALCOM:US30',
  SPX500: 'CAPITALCOM:US500',
  NAS100: 'CAPITALCOM:US100',
  UK100: 'CAPITALCOM:UK100',
  DE40: 'CAPITALCOM:DE40',
  JP225: 'CAPITALCOM:J225',
  // Stocks that are not on NASDAQ
  JPM: 'NYSE:JPM',
  V: 'NYSE:V',
  SPY: 'AMEX:SPY'
};

function toTvSymbol({ symbol, provider, assetClass }) {
  if (SYMBOL_OVERRIDES[symbol]) return SYMBOL_OVERRIDES[symbol];
  if (provider === 'binance') return `BINANCE:${symbol}`;
  if (assetClass === 'forex') return `FX:${symbol}`;
  if (provider === 'stocks' || assetClass === 'stocks') return `NASDAQ:${symbol}`;
  return symbol;
}

export const TradingViewChart = ({ symbol, interval, provider, assetClass, indicators = {} }) => {
  const containerRef = useRef(null);
  const idRef = useRef(`tv_${Math.random().toString(36).slice(2)}`);

  const tvSymbol = toTvSymbol({ symbol, provider, assetClass });
  const tvInterval = INTERVAL_MAP[interval] || '60';
  const studies = Object.keys(indicators)
    .filter((k) => indicators[k] && STUDY_MAP[k])
    .map((k) => STUDY_MAP[k]);

  useEffect(() => {
    let cancelled = false;
    loadTradingView()
      .then(() => {
        if (cancelled || !containerRef.current || !window.TradingView) return;
        containerRef.current.replaceChildren();
        const inner = document.createElement('div');
        inner.id = idRef.current;
        inner.style.height = '100%';
        inner.style.width = '100%';
        containerRef.current.appendChild(inner);
        /* eslint-disable no-new */
        new window.TradingView.widget({
          container_id: idRef.current,
          autosize: true,
          symbol: tvSymbol,
          interval: tvInterval,
          timezone: 'Etc/UTC',
          theme: 'dark',
          style: '1',
          locale: 'en',
          toolbar_bg: '#0a0f0d',
          enable_publishing: false,
          allow_symbol_change: false,
          hide_top_toolbar: false,
          hide_legend: false,
          withdateranges: true,
          studies,
          overrides: {
            'paneProperties.background': '#080c0a',
            'paneProperties.backgroundType': 'solid',
            'paneProperties.vertGridProperties.color': '#0f1714',
            'paneProperties.horzGridProperties.color': '#0f1714',
            'scalesProperties.textColor': '#8aa39b',
            'mainSeriesProperties.candleStyle.upColor': '#00e676',
            'mainSeriesProperties.candleStyle.downColor': '#ef4444',
            'mainSeriesProperties.candleStyle.borderUpColor': '#00e676',
            'mainSeriesProperties.candleStyle.borderDownColor': '#ef4444',
            'mainSeriesProperties.candleStyle.wickUpColor': '#00e676',
            'mainSeriesProperties.candleStyle.wickDownColor': '#ef4444'
          }
        });
      })
      .catch(() => {
        if (containerRef.current) {
          containerRef.current.replaceChildren();
          const msg = document.createElement('div');
          msg.style.cssText = 'display:flex;height:100%;align-items:center;justify-content:center;color:#8aa39b;font-size:12px';
          msg.textContent = 'Live chart unavailable — check your connection';
          containerRef.current.appendChild(msg);
        }
      });
    return () => { cancelled = true; };
  }, [tvSymbol, tvInterval, JSON.stringify(studies)]);

  return (
    <div
      ref={containerRef}
      data-testid="tradingview-chart"
      style={{ height: '100%', width: '100%', minHeight: 440 }}
    />
  );
};

export default TradingViewChart;
